// Reusable guided-tour component.
// Renders a small "Take a tour" pill in the corner of the page. Clicking it
// dims the page and walks the user through a sequence of spotlighted steps.
//
// Designed to be mounted anywhere — pass `steps`, an optional pill label and
// pill position, and you're done. No auto-start, no localStorage flag: this is
// purely pill-triggered, so it can sit on tabs/pages where some users would
// rather skip the walkthrough entirely.
//
// Each step matches a target via a CSS `selector` (e.g. '[data-tour="foo"]' or
// '.swagger-ui .opblock'). The spotlight follows the target on every animation
// frame, so the highlight stays glued even while the page reflows.
//
// For the landing-page first-visit experience, see OnboardingTour.js — that
// component owns its own "Want a tour?" prompt and localStorage handling.

import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

// Brand palette — kept in sync with the rest of the tour UI.
const fontMono = "'JetBrains Mono', 'Fira Mono', 'Menlo', monospace";
const yellow = "#D9FF5B";
const green = "#04c389";
const dark = "#1D1D1D";

// Below this viewport width the tour switches to its mobile layout
// (tooltip docks to top/bottom of viewport, pill labels shrink).
// Matches Tailwind's `sm` breakpoint so styles stay consistent with the app.
const MOBILE_BREAKPOINT = 640;

// Picks a viewport-safe top/left for the tooltip given the target rect and
// preferred placement. Result is clamped so the tooltip never sits off-screen
// (which would otherwise happen for targets near edges).
function computeTooltipPosition(rect, placement) {
  const margin = 16;
  // Approximate tooltip footprint — used only for viewport clamping.
  const tooltipW = 380;
  const tooltipH = 110;
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  let top, left;
  switch (placement) {
    case "right":
      top = rect.top;
      left = rect.right + margin;
      break;
    case "left":
      top = rect.top;
      left = rect.left - tooltipW - margin;
      break;
    case "bottom":
      top = rect.bottom + margin;
      left = rect.left;
      break;
    case "bottom-left":
      // Align the tooltip's right edge with the target's right edge
      // so it doesn't run off-screen for targets near the right gutter.
      top = rect.bottom + margin;
      left = Math.max(margin, rect.right - tooltipW);
      break;
    case "top":
    default:
      top = rect.top - tooltipH - margin;
      left = rect.left;
      break;
  }

  // Clamp inside the viewport so edge-placed targets stay visible.
  left = Math.min(Math.max(margin, left), vw - tooltipW - margin);
  top = Math.min(Math.max(margin, top), vh - tooltipH - margin);
  return { top, left };
}

// Floating tooltip rendered next to the spotlighted target.
// When `rect` is null (target not in the DOM / display:none on the current
// breakpoint), the tooltip falls back to a centred card so the user still
// sees the step text and isn't stranded on a dim screen with no instruction.
function Tooltip({ rect, step, onNext, onSkip, totalSteps, currentIndex, isMobile }) {
  // Mobile: pin to top or bottom of the viewport depending on where the
  // spotlight sits, so the tooltip never overlaps the highlighted element.
  // Desktop: position relative to target via `placement`.
  // No rect (hidden target): centre the tooltip horizontally near the top
  // and signal that the user may need to scroll / switch tabs to see the
  // referenced element.
  let style;
  if (!rect) {
    // Position the fallback well below typical nav bars so it doesn't get
    // hidden behind the page header on either desktop or mobile.
    style = { left: 12, right: 12, top: 130, maxWidth: "none" };
  } else if (isMobile) {
    // Pin to top or bottom of the viewport depending on where the spotlight
    // sits, so the tooltip never overlaps the highlighted element. The top
    // dock uses an offset (not just `top: 16`) so the card and its Finish/
    // Skip controls clear the fixed page navigation that lives at the top
    // of every authenticated page.
    const vh = typeof window !== "undefined" ? window.innerHeight : 800;
    const spotlightCenterY = rect.top + rect.height / 2;
    const dockTop = spotlightCenterY > vh / 2;
    style = dockTop
      ? { left: 12, right: 12, top: 100 }
      : { left: 12, right: 12, bottom: 16 };
  } else {
    style = { ...computeTooltipPosition(rect, step.placement || "bottom"), maxWidth: 360 };
  }

  return (
    <div
      className="fixed z-[10001] pointer-events-auto"
      style={{ ...style, fontFamily: fontMono }}
    >
      <div className="flex items-start gap-2">
        {/* Yellow dot — visual anchor between target and tooltip */}
        <span
          className="inline-block mt-2 flex-shrink-0 rounded-full"
          style={{ width: 14, height: 14, background: yellow }}
        />
        <div
          className="rounded-md px-3 py-2 text-white text-sm sm:text-base shadow-xl flex-1 min-w-0"
          style={{
            background: dark,
            border: "1px solid #3a3a3a",
            fontFamily: fontMono,
          }}
        >
          <div>{step.title}</div>
        </div>
      </div>

      {/* Controls row: progress counter + Next / Skip buttons */}
      <div className="flex items-center gap-2 mt-3 ml-6">
        <span className="text-xs text-white/60" style={{ fontFamily: fontMono }}>
          {currentIndex + 1} / {totalSteps}
        </span>
        <button
          onClick={onNext}
          className="rounded-full px-3 py-1 text-xs font-semibold"
          style={{ background: yellow, color: dark, fontFamily: fontMono }}
        >
          {currentIndex === totalSteps - 1 ? "Finish" : "Next"}
        </button>
        <button
          onClick={onSkip}
          className="rounded-full px-3 py-1 text-xs font-semibold border"
          style={{
            borderColor: "#ffffff55",
            color: "#fff",
            fontFamily: fontMono,
            background: "transparent",
          }}
        >
          Skip
        </button>
      </div>
    </div>
  );
}

// Returns the first DOM node matching `selector` that's actually visible.
// Pages that render the same content in multiple layout branches (e.g.
// PlaygroundLayout's mobile vs. desktop wrappers) will have several matching
// nodes; querySelector returns the first regardless of CSS visibility, which
// produces a spotlight glued to an off-screen / display:none clone. Walking
// the candidates and picking one with non-zero layout dimensions fixes that.
function findVisibleElement(selector) {
  const nodes = document.querySelectorAll(selector);
  for (const el of nodes) {
    // offsetParent is null when the element (or an ancestor) is display:none.
    // Combined with a positive bounding rect, this rules out hidden branches.
    if (el.offsetParent !== null && el.getBoundingClientRect().width > 0) {
      return el;
    }
  }
  return null;
}

// Props:
//   steps        — [{ selector, title, placement?, padding? }] (required)
//                  Used on desktop, and on mobile when `mobileSteps` is not set.
//   mobileSteps  — Optional separate step list for viewports below the mobile
//                  breakpoint. Use when desktop targets aren't all visible at
//                  once on mobile (e.g. tab-switched panels), so the tour can
//                  collapse those into a single combined step.
//   pillLabel    — text on the trigger pill (desktop). Default: "Take a tour".
//   pillShortLabel — text on the trigger pill (mobile). Default: "Tour".
//   pillTop      — distance from top in px. Default: 120. Ignored if pillBottom set.
//   pillBottom   — distance from bottom in px. If set, anchors the pill to the
//                  bottom of the viewport instead of the top. Use this on
//                  dense pages where the top-right would overlap content.
//   pillRight    — distance from right in px. Default: 24.
//   visible      — if false, the pill is hidden (useful when the host page
//                  conditionally provides the tour, e.g. only on certain tabs).
export default function TourRunner({
  steps,
  mobileSteps,
  pillLabel = "Take a tour",
  pillShortLabel = "Tour",
  pillTop = 120,
  pillBottom,
  pillRight = 24,
  visible = true,
}) {
  // Two phases: idle (pill visible) and running (spotlight + tooltip).
  // No "asking" / "final" / "done" phases — this component is pill-only.
  const [phase, setPhase] = useState("idle");
  const [stepIndex, setStepIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.innerWidth < MOBILE_BREAKPOINT
  );
  // Animation-frame handle for the live rect tracker.
  const rafRef = useRef(0);

  // Pick the right step list for the current breakpoint. Falls back to the
  // desktop `steps` when `mobileSteps` is not provided, so callers that don't
  // need a separate mobile flow keep working unchanged.
  const activeSteps = isMobile && mobileSteps ? mobileSteps : steps;

  // Keep `isMobile` in sync with viewport changes (e.g. rotation).
  // Reset to step 0 on a breakpoint flip — the two step lists may have
  // different lengths/targets, so the old index could point at the wrong step.
  useEffect(() => {
    const onResize = () => {
      setIsMobile((prev) => {
        const next = window.innerWidth < MOBILE_BREAKPOINT;
        if (next !== prev) setStepIndex(0);
        return next;
      });
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Keep the spotlight glued to the current target while the tour is running.
  // Re-measuring on every animation frame catches layout shifts that resize/
  // scroll alone wouldn't — image loads, font swaps, sticky-nav transitions.
  useLayoutEffect(() => {
    if (phase !== "running") return;
    const step = activeSteps[stepIndex];
    if (!step) return;

    const measure = () => {
      const el = findVisibleElement(step.selector);
      if (el) {
        const r = el.getBoundingClientRect();
        setRect({
          top: r.top,
          left: r.left,
          right: r.right,
          bottom: r.bottom,
          width: r.width,
          height: r.height,
        });
      } else {
        // Target not in the DOM yet — hide spotlight until it returns.
        setRect(null);
      }
    };

    const loop = () => {
      measure();
      rafRef.current = requestAnimationFrame(loop);
    };
    loop();

    // Safety-net listeners in case rAF is throttled (background tab etc).
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [phase, stepIndex, activeSteps]);

  // When the tour starts, scroll the first target into view so the spotlight
  // isn't sitting off-screen. Re-runs whenever the step changes.
  useEffect(() => {
    if (phase !== "running") return;
    const step = activeSteps[stepIndex];
    if (!step) return;
    const el = findVisibleElement(step.selector);
    if (el && el.scrollIntoView) {
      el.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
    }
  }, [phase, stepIndex, activeSteps]);

  // Prevent user scrolling (wheel, touch, keyboard) during active tour so the
  // spotlight stays aligned with the target element without visual artifacts.
  useEffect(() => {
    if (phase !== "running") return;

    const preventScroll = (e) => {
      e.preventDefault();
    };

    const preventKeyScroll = (e) => {
      if (["ArrowUp", "ArrowDown", "PageUp", "PageDown", "Home", "End", " "].includes(e.key)) {
        if (!["INPUT", "TEXTAREA"].includes(e.target?.tagName)) {
          e.preventDefault();
        }
      }
    };

    window.addEventListener("wheel", preventScroll, { passive: false });
    window.addEventListener("touchmove", preventScroll, { passive: false });
    window.addEventListener("keydown", preventKeyScroll);

    return () => {
      window.removeEventListener("wheel", preventScroll);
      window.removeEventListener("touchmove", preventScroll);
      window.removeEventListener("keydown", preventKeyScroll);
    };
  }, [phase]);

  const handleStart = () => {
    setStepIndex(0);
    setPhase("running");
  };

  const handleNext = () => {
    if (stepIndex < activeSteps.length - 1) {
      setStepIndex(stepIndex + 1);
    } else {
      // Finished — return to the idle pill state.
      setStepIndex(0);
      setPhase("idle");
    }
  };

  const handleSkip = () => {
    setStepIndex(0);
    setPhase("idle");
  };

  if (!visible) return null;

  // Idle: render the trigger pill.
  if (phase === "idle") {
    // pillBottom takes precedence over pillTop — anchors to viewport bottom.
    const pillPosition =
      pillBottom !== undefined
        ? { bottom: pillBottom, right: pillRight }
        : { top: pillTop, right: pillRight };
    return (
      <button
        onClick={handleStart}
        className="fixed z-[10001] flex items-center gap-2 sm:gap-3 px-3 sm:px-5 py-2 sm:py-3 rounded-full shadow-2xl hover:scale-105 transition-transform"
        style={{
          ...pillPosition,
          background: dark,
          border: `2px solid ${yellow}`,
          fontFamily: fontMono,
          boxShadow: `0 0 0 4px rgba(217,255,91,0.15), 0 10px 30px rgba(0,0,0,0.5)`,
        }}
        aria-label={pillLabel}
      >
        <span
          className="inline-block rounded-full animate-pulse"
          style={{ width: 12, height: 12, background: yellow }}
        />
        <span className="text-white text-xs sm:text-base font-semibold whitespace-nowrap">
          <span className="hidden sm:inline">{pillLabel}</span>
          <span className="sm:hidden">{pillShortLabel}</span>
        </span>
      </button>
    );
  }

  // Running: dim overlay + spotlight cutout + tooltip.
  const step = activeSteps[stepIndex];
  const pad = (step && step.padding) || 8;
  const radius = 12;
  let hole = null;
  if (rect) {
    const rawTop = rect.top - pad;
    const rawLeft = rect.left - pad;
    const rawRight = rect.right + pad;
    const rawBottom = rect.bottom + pad;

    const vw = typeof window !== "undefined" ? window.innerWidth : 1000;
    const vh = typeof window !== "undefined" ? window.innerHeight : 1000;

    const clampedTop = Math.max(0, Math.min(rawTop, vh));
    const clampedLeft = Math.max(0, Math.min(rawLeft, vw));
    const clampedRight = Math.max(0, Math.min(rawRight, vw));
    const clampedBottom = Math.max(0, Math.min(rawBottom, vh));

    const w = clampedRight - clampedLeft;
    const h = clampedBottom - clampedTop;

    if (w > 0 && h > 0) {
      hole = { x: clampedLeft, y: clampedTop, w, h };
    }
  }

  return (
    // pointer-events:none on outer layer keeps the page interactive where we
    // don't explicitly opt in (the SVG dim and the Tooltip both opt in).
    <div className="fixed inset-0 z-[10000] pointer-events-none">
      <svg
        className="absolute inset-0 w-full h-full pointer-events-auto"
        style={{ display: "block" }}
      >
        <defs>
          {/* White = visible dim, black = cutout. The black rect punches the hole. */}
          <mask id={`tour-mask-${stepIndex}`}>
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {hole && (
              <rect
                x={hole.x}
                y={hole.y}
                width={hole.w}
                height={hole.h}
                rx={radius}
                ry={radius}
                fill="black"
              />
            )}
          </mask>
        </defs>
        {/* Dim layer. Clicking it advances the tour. */}
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="rgba(0,0,0,0.65)"
          mask={`url(#tour-mask-${stepIndex})`}
          onClick={handleNext}
        />
        {/* Green outline around the spotlight for emphasis. */}
        {hole && (
          <rect
            x={hole.x}
            y={hole.y}
            width={hole.w}
            height={hole.h}
            rx={radius}
            ry={radius}
            fill="none"
            stroke={green}
            strokeWidth="2"
            pointerEvents="none"
          />
        )}
      </svg>

      <Tooltip
        rect={rect}
        step={step}
        onNext={handleNext}
        onSkip={handleSkip}
        totalSteps={activeSteps.length}
        currentIndex={stepIndex}
        isMobile={isMobile}
      />
    </div>
  );
}
